/**
 * First-party partner/lead attribution – independent of analytics cookie consent.
 * Stored on quotes.attribution_snapshot alongside optional analytics attribution.
 *
 * SE→FEST: partner params persist in sessionStorage so navigation on FEST
 * does not overwrite source_site / original source_page.
 */

import { classifyB2bSegment } from './b2bSegmentClassification';

export type PartnerLeadAttribution = {
  partner?: string | null;
  skaneevent_path?: string | null;
  cta_context?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  budget_segment?: string | null;
  event_type?: string | null;
  full_service_preference?: string | null;
  form_variant?: string | null;
  /** Original acquisition page (SE path or FEST landing) — not /offert/event overwrite. */
  source_page?: string | null;
  /** First-touch landing when known. */
  landing_page?: string | null;
  /** CTA label/type when known (planning_call, quote, hero, etc.). */
  cta_type?: string | null;
  /**
   * Forward-looking acquisition identity (not retroactive).
   * `source_site`: festutrustning | skaneevent
   * `source_domain`: festutrustning.se | skaneevent.se
   */
  source_site?: 'festutrustning' | 'skaneevent' | string | null;
  source_domain?: string | null;
};

/** sessionStorage — consent-independent partner lead (same policy as URL params). */
export const PARTNER_LEAD_STORAGE_KEY = 'entaPartnerLeadAttribution';

export function resolveAcquisitionSourceIdentity(
  partnerLead: PartnerLeadAttribution,
): { source_site: 'festutrustning' | 'skaneevent'; source_domain: string } {
  if (
    partnerLead.partner === 'skaneevent' ||
    partnerLead.utm_source === 'skaneevent' ||
    Boolean(partnerLead.skaneevent_path) ||
    partnerLead.source_site === 'skaneevent'
  ) {
    return { source_site: 'skaneevent', source_domain: 'skaneevent.se' };
  }
  return { source_site: 'festutrustning', source_domain: 'festutrustning.se' };
}

export type ForetagHubNeedCard = 'foretagsevent' | 'konferens' | 'massa';

export type B2bRequestMetadata = {
  company_name?: string | null;
  venue?: string | null;
  needs?: string[];
  full_service_preference?: string | null;
  budget_segment?: string | null;
  hub_need_card?: ForetagHubNeedCard | null;
};

export const SKANE_EVENT_PACKAGE_TYPE = 'Skåne Event – eventförfrågan';
export const FORETAG_NEED_PACKAGE_TYPE = 'Företag – behovsförfrågan';

export function hubNeedCardFromCtaContext(
  ctaContext: string | null | undefined,
): ForetagHubNeedCard | null {
  if (!ctaContext?.startsWith('foretag_need_')) return null;
  const suffix = ctaContext.slice('foretag_need_'.length);
  if (suffix === 'foretagsevent' || suffix === 'konferens' || suffix === 'massa') {
    return suffix;
  }
  return null;
}

export function resolveOffertEventPackageType(
  partnerLead: PartnerLeadAttribution,
): string {
  if (partnerLead.cta_context?.startsWith('foretag_need_')) {
    return FORETAG_NEED_PACKAGE_TYPE;
  }
  if (isSkaneEventPartner(partnerLead) || partnerLead.utm_source === 'skaneevent') {
    return SKANE_EVENT_PACKAGE_TYPE;
  }
  return SKANE_EVENT_PACKAGE_TYPE;
}

function nonEmpty(v: string | null | undefined): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s.length ? s : null;
}

export function parsePartnerParamsFromSearch(search: string): PartnerLeadAttribution {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const utmSource = params.get('utm_source');
  const explicitPartner = params.get('partner');

  let partner: string | null = explicitPartner;
  if (!partner && utmSource === 'skaneevent') {
    partner = 'skaneevent';
  }

  const skRef = params.get('sk_ref');
  const lead: PartnerLeadAttribution = {
    partner,
    skaneevent_path: skRef,
    cta_context: params.get('cta_context'),
    utm_source: utmSource,
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    cta_type: params.get('cta_type') || params.get('cta_context'),
  };

  if (partner === 'skaneevent' || utmSource === 'skaneevent' || skRef) {
    lead.source_site = 'skaneevent';
    lead.source_domain = 'skaneevent.se';
    lead.source_page = skRef || lead.source_page || null;
    lead.landing_page = skRef || null;
  }

  return lead;
}

export function parsePartnerParamsFromWindow(): PartnerLeadAttribution {
  if (typeof window === 'undefined') return {};
  return parsePartnerParamsFromSearch(window.location.search || '');
}

export function readPersistedPartnerLead(): PartnerLeadAttribution | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PARTNER_LEAD_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PartnerLeadAttribution;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Merge URL lead with session-persisted lead.
 * Non-empty URL fields win; SE identity never cleared by empty FEST URL.
 */
export function mergePartnerLead(
  fromUrl: PartnerLeadAttribution,
  fromStore: PartnerLeadAttribution | null | undefined,
): PartnerLeadAttribution {
  const base = fromStore ?? {};
  const merged: PartnerLeadAttribution = { ...base };

  const keys: (keyof PartnerLeadAttribution)[] = [
    'partner',
    'skaneevent_path',
    'cta_context',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'budget_segment',
    'event_type',
    'full_service_preference',
    'form_variant',
    'source_page',
    'landing_page',
    'cta_type',
    'source_site',
    'source_domain',
  ];

  for (const key of keys) {
    const next = nonEmpty(fromUrl[key] as string | null | undefined);
    if (next != null) {
      (merged as Record<string, unknown>)[key] = next;
    }
  }

  const acquisition = resolveAcquisitionSourceIdentity(merged);
  merged.source_site = acquisition.source_site;
  merged.source_domain = acquisition.source_domain;

  if (acquisition.source_site === 'skaneevent') {
    merged.partner = merged.partner || 'skaneevent';
    if (merged.skaneevent_path) {
      // Keep original SE path as source_page even if URL later is /offert/event
      merged.source_page = merged.skaneevent_path;
      merged.landing_page = merged.landing_page || merged.skaneevent_path;
    }
  }

  return merged;
}

export function persistPartnerLead(lead: PartnerLeadAttribution): PartnerLeadAttribution {
  if (typeof window === 'undefined') return lead;
  const existing = readPersistedPartnerLead();
  const merged = mergePartnerLead(lead, existing);
  // Only persist when there is meaningful acquisition signal
  if (
    merged.partner ||
    merged.skaneevent_path ||
    merged.utm_source ||
    merged.source_page ||
    merged.cta_context
  ) {
    try {
      sessionStorage.setItem(PARTNER_LEAD_STORAGE_KEY, JSON.stringify(merged));
    } catch {
      /* ignore quota */
    }
  }
  return merged;
}

/** Resolve partner lead from current URL + session persistence (SE→FEST safe). */
export function resolvePartnerLeadFromSearch(search: string): PartnerLeadAttribution {
  const fromUrl = parsePartnerParamsFromSearch(search);
  const stored = readPersistedPartnerLead();
  const merged = mergePartnerLead(fromUrl, stored);
  if (
    fromUrl.partner ||
    fromUrl.skaneevent_path ||
    fromUrl.utm_source ||
    fromUrl.cta_context
  ) {
    return persistPartnerLead(fromUrl);
  }
  return merged;
}

export function resolvePartnerLeadFromWindow(): PartnerLeadAttribution {
  if (typeof window === 'undefined') return {};
  return resolvePartnerLeadFromSearch(window.location.search || '');
}

export function isSkaneEventPartner(attribution: PartnerLeadAttribution | null | undefined): boolean {
  return (
    attribution?.partner === 'skaneevent' ||
    attribution?.source_site === 'skaneevent' ||
    Boolean(attribution?.skaneevent_path)
  );
}

function analyticsLanding(analyticsSnapshot?: Record<string, unknown> | null): string | null {
  if (!analyticsSnapshot) return null;
  const first = analyticsSnapshot.first;
  if (first && typeof first === 'object') {
    const t = first as Record<string, unknown>;
    return nonEmpty(t.landingPage as string) || nonEmpty(t.landing_page as string);
  }
  return nonEmpty(analyticsSnapshot.landing_path as string);
}

function analyticsReferrer(analyticsSnapshot?: Record<string, unknown> | null): string | null {
  if (!analyticsSnapshot) return null;
  const first = analyticsSnapshot.first;
  if (first && typeof first === 'object') {
    const t = first as Record<string, unknown>;
    return (
      nonEmpty(t.externalReferrer as string) || nonEmpty(t.external_referrer as string)
    );
  }
  return nonEmpty(analyticsSnapshot.external_referrer as string);
}

export function buildQuoteAttributionSnapshot(opts: {
  partnerLead: PartnerLeadAttribution;
  analyticsSnapshot?: Record<string, unknown> | null;
  formOverrides?: Pick<
    PartnerLeadAttribution,
    'budget_segment' | 'event_type' | 'full_service_preference'
  >;
  packageType?: string | null;
  productId?: string | null;
  formVariant?: string | null;
  hubNeedCard?: string | null;
  ctaType?: string | null;
}): Record<string, unknown> {
  const conversionPage =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search || ''}`
      : null;

  const acquisition = resolveAcquisitionSourceIdentity(opts.partnerLead);
  const festLanding = analyticsLanding(opts.analyticsSnapshot);

  // Original acquisition page — NEVER overwrite SE path with FEST /offert/event
  let sourcePage: string | null = null;
  if (acquisition.source_site === 'skaneevent') {
    sourcePage =
      nonEmpty(opts.partnerLead.skaneevent_path) ||
      nonEmpty(opts.partnerLead.source_page) ||
      null;
  } else {
    sourcePage =
      nonEmpty(opts.partnerLead.source_page) ||
      festLanding ||
      (conversionPage && !/\/offert\/event/i.test(conversionPage)
        ? conversionPage.split('?')[0]
        : null) ||
      festLanding;
  }

  const landingPage =
    acquisition.source_site === 'skaneevent'
      ? nonEmpty(opts.partnerLead.landing_page) ||
        nonEmpty(opts.partnerLead.skaneevent_path) ||
        sourcePage
      : festLanding || sourcePage;

  const eventType =
    opts.formOverrides?.event_type ?? opts.partnerLead.event_type ?? null;
  const ctaContext = opts.partnerLead.cta_context ?? null;
  const ctaType =
    opts.ctaType || opts.partnerLead.cta_type || ctaContext || null;

  const classification = classifyB2bSegment({
    event_type: eventType,
    hub_need_card: opts.hubNeedCard ?? hubNeedCardFromCtaContext(ctaContext),
    cta_context: ctaContext,
    utm_campaign: opts.partnerLead.utm_campaign,
    source_page: sourcePage,
    skaneevent_path: opts.partnerLead.skaneevent_path,
    landing_page: landingPage,
    form_variant: opts.formVariant ?? opts.partnerLead.form_variant,
    package_type: opts.packageType,
  });

  const snapshot: Record<string, unknown> = {
    ...(opts.analyticsSnapshot ?? {}),
    // Acquisition identity: partner/SE wins — analytics must not overwrite SE→FEST
    source_site: acquisition.source_site,
    source_domain: acquisition.source_domain,
    source_page: sourcePage,
    landing_page: landingPage,
    conversion_page: conversionPage,
    quote_page: conversionPage,
    referrer: analyticsReferrer(opts.analyticsSnapshot),
    cta_type: ctaType,
    package_type: opts.packageType ?? null,
    product_id: opts.productId ?? null,
    form_variant: opts.formVariant ?? opts.partnerLead.form_variant ?? null,
    b2b_segment: classification.segment,
    b2b_classification_method: classification.method,
    b2b_classification_evidence: classification.evidence,
    attribution_timestamp: new Date().toISOString(),
  };

  const leadKeys: (keyof PartnerLeadAttribution)[] = [
    'partner',
    'skaneevent_path',
    'cta_context',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'budget_segment',
    'event_type',
    'full_service_preference',
  ];

  for (const key of leadKeys) {
    const fromForm = opts.formOverrides?.[key as keyof typeof opts.formOverrides];
    const fromUrl = opts.partnerLead[key];
    const value = fromForm ?? fromUrl;
    if (value != null && value !== '') {
      snapshot[key] = value;
    }
  }

  // Channel from UTM when analytics consent snapshot thin
  if (!snapshot.channel && opts.partnerLead.utm_source) {
    snapshot.channel =
      opts.partnerLead.utm_source === 'skaneevent' ? 'referral' : 'campaign';
  }

  return snapshot;
}

export function readAttributionSnapshot(
  raw: unknown
): PartnerLeadAttribution | null {
  if (!raw || typeof raw !== 'object') return null;
  return raw as PartnerLeadAttribution;
}

export function readRequestMetadata(raw: unknown): B2bRequestMetadata | null {
  if (!raw || typeof raw !== 'object') return null;
  return raw as B2bRequestMetadata;
}
